import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { useInspectionStore } from "@/stores/inspection";
import { INSPECTION_COMMENTS, STRUCTURE_UNIT_TYPE } from "@/constants/Inspections";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { styles } from "@/reports/reportStyles";
import { convertToInt } from "@/composables/util";
import { CUSTOM_ICONS } from "@/constants/Unicode";

function setJointsDocDefinition(structure) {
  const configStore = useConfigStore();
  const jointDetailsOne = [];
  jointDetailsOne.push(getJointsOneHeader());
  getJointOne(jointDetailsOne, structure);

  let docDefinition = {
    header: function () {
      return getReportHeader("Joints Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Joints Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Joint Conditions",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      {
        columns: [
          {
            width: "40%",
            text: "B.C.08 Overall Joint Condition Rating: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.JOINT_CONDITION_RATING,
              structure?.userInspection?.overallJointCondRating,
            ),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        text: "Overall Joint Notes: ",
        bold: true,
        margin: [0, 3, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.JOINTS_TINSP_COMMENTS,
        )?.notes,
        margin: [0, 0, 0, 5],
      },
      {
        table: {
          widths: ["100%"],
          headerRows: 0,
          body: [
            ["Legend:"],
            [
              {
                table: {
                  headerRows: 0,
                  widths: ["25%", "25%", "25%", "25%"],
                  body: [
                    ["JT.01 Joint Key", "JT.04 Joint Location", "JT.08 Debris Impaction?", "JT.11 Covered?"],
                    ["JT.02 Record Key", "JT.05 Joint Movement", "JT.09 Leaking?", "JT.12 Condition Rating"],
                    ["JT.03 Joint Type", "JT.07 Joint Length (ft)", "JT.10 Damage?", ""],
                  ],
                },
                layout: "noBorders",
              },
            ],
          ],
        },
        layout: {
          hLineWidth: function (i, node) {
            return i === 0 || i === node.table.body.length ? 1 : 0;
          },
          vLineWidth: function (i, node) {
            return i === 0 || i === node.table.widths.length ? 1 : 0;
          },
          hLineColor: function () {
            return "black";
          },
          vLineColor: function () {
            return "black";
          },
        },
        unbreakable: true,
      },
      {
        table: {
          headerRows: 1,
          widths: ["10%", "auto", "auto", "*", "7%", "auto", "auto", "auto", "auto", "auto"],
          body: jointDetailsOne,
        },
        margin: [0, 5, 0, 0],
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  return docDefinition;
}

function getJointsOneHeader() {
  return [
    {
      text: "JT.01-JT.02",
      style: ["tableHeader"],
    },
    {
      text: "JT.03",
      style: ["tableHeader"],
    },
    {
      text: "JT.04",
      style: ["tableHeader"],
    },
    {
      text: "JT.05",
      style: ["tableHeader"],
    },
    {
      text: "JT.07",
      style: ["tableHeader"],
    },
    {
      text: "JT.08",
      style: ["tableHeader"],
    },
    {
      text: "JT.09",
      style: ["tableHeader"],
    },
    {
      text: "JT.10",
      style: ["tableHeader"],
    },
    {
      text: "JT.11",
      style: ["tableHeader"],
    },
    {
      text: "JT.12",
      style: ["tableHeader"],
    },
  ];
}
function getJointLocations() {
  const configStore = useConfigStore();
  const inspectionStore = useInspectionStore();
  return inspectionStore
    .getFilteredStructureUnits([STRUCTURE_UNIT_TYPE.APRAS_SPAN], false)
    ?.map((b) => ({
      title: `${configStore.getReferenceValue(REFERENCE_TABLE.STRUCTURE_UNIT_TYPE, b?.legacyStructureType, true)}-${
        b?.strUnitLabel
      }`,
      value: b?.strUnitKey,
    }))
    .sort((a, b) => a?.title?.localeCompare(b?.title));
}

function getJointOne(jointDetailsOne, structure) {
  const jointLocations = getJointLocations();
  const configStore = useConfigStore();
  const joints =
    structure?.structureJoints?.sort(function (a, b) {
      if (a?.jointId == b?.jointId) {
        return convertToInt(b?.recordKey) - convertToInt(a?.recordKey);
      } else {
        return a?.jointId - b?.jointId;
      }
    }) || [];
  for (const joint of joints ?? []) {
    jointDetailsOne.push(
      [
        {
          text: joint?.jointId + "-" + joint?.recordKey,
          style: ["tableBody"],
        },
        {
          text: configStore.getReferenceValue(REFERENCE_TABLE.JOINT_TYPE, joint?.jointType),
          style: ["tableBody"],
        },
        {
          text: jointLocations?.find((a) => a.value === joint?.jointLocation)?.title,
          style: ["tableBody"],
        },
        {
          text: configStore.getReferenceValue(REFERENCE_TABLE.JOINT_MOVEMENT, joint?.jointMovement),
          style: ["tableBody"],
        },
        {
          text: joint?.jointLength || joint?.jointLength == 0 ? Number.parseInt(joint?.jointLength) + " ft" : "",
          style: ["tableBody"],
        },
        {
          text: joint?.debrisImpact == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
          style: ["customFontAwesomeTableBody"],
        },
        {
          text: joint?.leaking == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
          style: ["customFontAwesomeTableBody"],
        },
        {
          text: joint?.damage == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
          style: ["customFontAwesomeTableBody"],
        },
        {
          text: joint?.covered == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
          style: ["customFontAwesomeTableBody"],
        },
        {
          text: configStore.getReferenceValue(REFERENCE_TABLE.JOINT_CONDITION_RATING, joint?.conditionRating),
          style: ["tableBody"],
        },
      ],
      //Joint notes
      [
        {
          colSpan: "10",
          text: [{ text: "JT.13 Joint Notes: ", bold: true }, joint?.jointComment],
        },
      ],
    );
  }
}

export { setJointsDocDefinition };
